import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  Platform,
  Image
} from 'react-native';
import { useTranslation } from "../../../../../i18n/useTranslation";
import identificationsData from '../../../../../constants/identifications.json';
import GroupId from "../../../../../../../../assets/images/IdentificationType.svg";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import DrivingLicenseIcon from "../../../../../../../../assets/images/DrivingIcon.svg";
import IdCard from "../../../../../../../../assets/images/PersonIcon.svg";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from '../../../../../constants/colors';
import { ComponentStyles } from "../../../../../constants/componentStyles";

interface IDType {
  key: string;
  EnglishLabel: string;
  SpanishLabel: string;
  Value?: string;
  photoIdCountry?: string;
  photoIdState?: string;
}

interface SenderIdentificationTypeProps {
  onSelectionChange?: (selectedId: string | null) => void;
  onCountrySelected?: () => void;
  onValidationChange?: (isValid: boolean) => void;
}

const ReceiverIdentificationTypeStep: React.FC<SenderIdentificationTypeProps> = ({
  onSelectionChange,
  onCountrySelected,
  onValidationChange
}) => {
  const { t, i18n } = useTranslation();
  const storedIdTypeKey = useSessionStore((s: SessionState) => (s as any).selectedIdTypeKey as string | null | undefined);
  const [selectedId, setSelectedId] = useState<string | null>(
    () => (useSessionStore.getState() as any).selectedIdTypeKey ?? null
  );
  const [idTypes, setIdTypes] = useState<IDType[]>([]);
  const setSelectedIdTypeKey = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeKey);
  const setSelectedIdTypeLabel = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeLabel);
  const setSelectedIdPhotoIdCountry = useSessionStore((s: SessionState) => (s as any).setSelectedIdPhotoIdCountry);
  const setSelectedIdPhotoIdStateAbbr = useSessionStore((s: SessionState) => (s as any).setSelectedIdPhotoIdStateAbbr);
  const setSelectedIdPhotoIdStateName = useSessionStore((s: SessionState) => (s as any).setSelectedIdPhotoIdStateName);
  const setSelectedIdTypeValue = useSessionStore((s: SessionState) => (s as any).setSelectedIdTypeValue);

  const isValid = selectedId != null ;
    
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  useEffect(() => {
    setSelectedId(storedIdTypeKey ?? null);
  }, [storedIdTypeKey]);
  
  useEffect(() => {
    const raw: any = identificationsData as any;
    let list: IDType[] = [];
    if (Array.isArray(raw?.IDTypes)) {
      list = (raw.IDTypes as any[]).map((x: any) => ({
        key: x.key ?? x.IDkey ?? x.Value,
        EnglishLabel: x.EnglishLabel,
        SpanishLabel: x.SpanishLabel,
        Value: x.Value,
        photoIdCountry: x.photoIdCountry,
        photoIdState: x.photoIdState,
      }));
    } else if (raw && typeof raw === 'object') {
      list = Object.entries(raw).map(([key, v]: [string, any]) => ({
        key,
        EnglishLabel: v?.EnglishLabel,
        SpanishLabel: v?.SpanishLabel,
        Value: v?.Value,
        photoIdCountry: v?.photoIdCountry,
        photoIdState: v?.photoIdState,
      }));
    }
    setIdTypes(list.filter(x => x && x.key));
  }, []);

  const handleSelection = (idKey: string) => {
    setSelectedId(idKey);
    onSelectionChange?.(idKey);
    try {
      setSelectedIdTypeKey?.(idKey);
      const selected = idTypes.find((x) => x.key === idKey);
      const label = selected ? (i18n.language === 'en' ? selected.EnglishLabel : selected.SpanishLabel) : null;
      setSelectedIdTypeLabel?.(label);
      const photoIdCountry = selected?.photoIdCountry ?? null;
      const photoIdState = selected?.photoIdState ?? null;
      setSelectedIdPhotoIdCountry?.(photoIdCountry);
      if (photoIdState) {
        setSelectedIdPhotoIdStateAbbr?.(photoIdState);
      } else {
        setSelectedIdPhotoIdStateAbbr?.(null);
        setSelectedIdPhotoIdStateName?.(null);
      }
      setSelectedIdTypeValue?.(selected?.Value ?? null);
      // Move to next screen 
      setTimeout(() => {
        onCountrySelected?.();
      }, 200);
    } catch {}
  };

  const handleContinue = () => {
    if (selectedId) {
      onCountrySelected?.();
    }
  };

  const getDisplayLabel = (idType: IDType) => {
    return i18n.language === 'en' ? idType.EnglishLabel : idType.SpanishLabel;
  };

  const getIconForIdType = (value?: string) => {
    // Return different icons based on ID type
    switch (value) {
      case 'DRV':
        return DrivingLicenseIcon; // Car icon for driver's license
      case 'PHT':
        return IdCard; // ID card icon for photo ID
      default:
        return IdCard;
    }
  };

  const Wrapper = Platform.OS === 'web' ? View : SafeAreaView;

  return (
    <Wrapper style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        <View style={styles.content}>
          {/* Header Icon */}
          <View style={styles.iconContainer}>
            <GroupId width={88} height={88}/>
          </View>

          {/* Title and Subtitle */}
          <Text style={styles.title}>{t('IDENTIFICATION_TYPE_TITLE')}</Text>
          <Text style={styles.subtitle}>{t('IDENTIFICATION_TYPE_SUBTITLE')}</Text>

          {/* Identification Types List */}
          <View style={styles.listContainer}>
            {idTypes.map((idType, index) => (
              <TouchableOpacity
                testID={`receiverIdentificationTypeOption`}
                key={index}
                style={[
                  styles.listItem,
                  // selectedId === idType.IDkey && styles.selectedItem,
                ]}
                onPress={() => handleSelection(idType.key)}
              >
                <View style={styles.itemContent}>
                  <View style={styles.itemLeft}>
                    {idType.Value === 'DRV' ?
                    (<DrivingLicenseIcon  width={24} height={24}/>)
                    : (<IdCard  width={24} height={24}/>)}
                    <Text style={styles.itemText}>
                      {getDisplayLabel(idType)}
                    </Text>
                  </View>
                    <View
                      style={[
                        styles.radioButton,
                      ]}
                    >
                      {selectedId === idType.key && (
                        <RadioSelectedIcon width={20} height={20}/>
                      )}
                    </View>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </ScrollView>
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24
  },
  scrollView: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingTop: 20,
    backgroundColor: '#FFFFFF',
  },
  iconContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  iconCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#E3F2FD',
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#2196F3',
  },
  iconText: {
    fontSize: 32,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  listContainer: {
    marginBottom: 20,
    width: '100%',
  },
  listItem: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    // marginBottom: 8,
    borderBottomWidth:1,
    borderColor: '#D9D9D9',
  },
  selectedItem: {
    borderColor: '#3B82F6',
    backgroundColor: '#F8FAFF',
  },
  itemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 24,
  },
  itemLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  itemIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  itemText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.darkGrey,
    paddingLeft: 8
  },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#D1D1D1'
  },
  buttonContainer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
    paddingTop: 10,
  },
  continueButton: {
    backgroundColor: '#1F2937',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  continueButtonDisabled: {
    backgroundColor: '#9CA3AF',
  },
  continueButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ReceiverIdentificationTypeStep;
